<template>
  <div class="step-content">
    <h5 class="mb-3">Documentação Jurídica</h5>

    <b-alert variant="info" show class="mb-4">
      <div class="alert-body">
        <h6 class="alert-heading">
          <i class="feather icon-info mr-1"></i>
          Documentos Necessários
        </h6>
        <hr>
        <p class="mb-2">
          <strong>• Formato aceito:</strong> PDF (máximo 5MB por arquivo)
        </p>
        <p class="mb-2">
          <strong>• Qualidade:</strong> Documentos devem estar legíveis e completos
        </p>
        <p class="mb-0">
          <strong>• Campos indicados como obrigatórios devem ser preenchidos</strong>
        </p>
      </div>
    </b-alert>

    <!-- Estatuto -->
    <b-row>
      <b-col md="12">
        <b-form-group label="Estatuto/Ato Constitutivo" label-class="required" label-for="statute">
          <b-form-file
            id="statute"
            :value="formData.statute ? formData.statute.file : null"
            placeholder="Escolha o arquivo ou arraste aqui..."
            drop-placeholder="Solte o arquivo aqui..."
            accept="application/pdf"
            :state="getFieldState('statute')"
            :disabled="disabled"
            @input="updateField('statute', $event)"
          />
          <template v-if="formData && formData.statute">
            <small v-if="formData.statute.uploaded" class="form-text text-success">
              <i class="feather icon-check-circle mr-1"></i>
              Arquivo enviado com sucesso
            </small>
            <small v-else-if="formData.statute.file" class="form-text text-warning">
              <i class="feather icon-upload mr-1"></i>
              Enviando arquivo...
            </small>
          </template>
          <b-form-invalid-feedback>
            Estatuto é obrigatório
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Ata de Eleição -->
    <b-row>
      <b-col md="12">
        <b-form-group label="Ata de Eleição de Dirigentes" label-class="required" label-for="election_minutes">
          <b-form-file
            id="election_minutes"
            :value="formData.election_minutes ? formData.election_minutes.file : null"
            placeholder="Escolha o arquivo ou arraste aqui..."
            drop-placeholder="Solte o arquivo aqui..."
            accept="application/pdf"
            :state="getFieldState('election_minutes')"
            :disabled="disabled"
            @input="updateField('election_minutes', $event)"
          />
          <template v-if="formData && formData.election_minutes">
            <small v-if="formData.election_minutes.uploaded" class="form-text text-success">
              <i class="feather icon-check-circle mr-1"></i>
              Arquivo enviado com sucesso
            </small>
            <small v-else-if="formData.election_minutes.file" class="form-text text-warning">
              <i class="feather icon-upload mr-1"></i>
              Enviando arquivo...
            </small>
          </template>
          <b-form-invalid-feedback>
            Ata de Eleição é obrigatória
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Certificado CNPJ -->
    <b-row>
      <b-col md="12">
        <b-form-group label="Certificado CNPJ" label-class="required" label-for="cnpjCertificate">
          <b-form-file
            id="cnpjCertificate"
            :value="formData.cnpj_certificate ? formData.cnpj_certificate.file : null"
            placeholder="Escolha o arquivo ou arraste aqui..."
            drop-placeholder="Solte o arquivo aqui..."
            accept="application/pdf"
            :state="getFieldState('cnpj_certificate')"
            :disabled="disabled"
            @input="updateField('cnpj_certificate', $event)"
          />
          <small v-if="formData.cnpj_certificate && isFileUploaded('cnpj_certificate')" class="form-text text-success">
            <i class="feather icon-check-circle mr-1"></i>
            Arquivo enviado: {{ getFileName('cnpj_certificate') }}
          </small>
          <small v-else-if="formData.cnpj_certificate && !isFileUploaded('cnpj_certificate')" class="form-text text-warning">
            <i class="feather icon-upload mr-1"></i>
            Enviando arquivo: {{ getFileName('cnpj_certificate') }}
          </small>
          <b-form-invalid-feedback>
            Certificado CNPJ é obrigatório
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Identificação dos integrantes do corpo dirigente de cada instituição -->
    <b-row>
      <b-col md="12">
        <b-form-group label="Identificação dos integrantes do corpo dirigente de cada instituição" label-class="required" label-for="identification_members">
          <b-form-file
            id="identificationMembers"
            :value="formData.identification_members ? formData.identification_members.file : null"
            placeholder="Escolha o arquivo ou arraste aqui..."
            drop-placeholder="Solte o arquivo aqui..."
            accept="application/pdf"
            :state="getFieldState('identification_members')"
            :disabled="disabled"
            @input="updateField('identification_members', $event)"
          />
          <small v-if="formData.identification_members && isFileUploaded('identification_members')" class="form-text text-success">
            <i class="feather icon-check-circle mr-1"></i>
            Arquivo enviado: {{ getFileName('identification_members') }}
          </small>
          <small v-else-if="formData.identification_members && !isFileUploaded('identification_members')" class="form-text text-warning">
            <i class="feather icon-upload mr-1"></i>
            Enviando arquivo: {{ getFileName('identification_members') }}
          </small>
          <b-form-invalid-feedback>
            Certificado digital é obrigatório
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Lista de arquivos carregados -->
    <div v-if="hasUploadedFiles" class="uploaded-files mt-4">
      <h6 class="text-muted mb-3">
        <i class="feather icon-check-circle mr-1"></i>
        Arquivos Carregados
      </h6>
      <b-card>
        <b-list-group>
          <b-list-group-item
            v-for="(file, key) in uploadedFiles"
            :key="key"
            class="d-flex justify-content-between align-items-center"
          >
            <div>
              <i class="feather icon-file-text text-danger mr-2"></i>
              <strong>{{ getDocumentTitle(key) }}</strong>
              <div class="text-muted small">
                <a v-if="getJuridicalDocumentUrl(uploadedFiles[key])" :href="getJuridicalDocumentUrl(uploadedFiles[key])" target="_blank" class="mr-1 no-print download-link text-primary" title="Baixar arquivo">
                  <i class="feather icon-download"></i>
                </a>
                {{ getFileName(key) }}
                <small class="ml-2">({{ formatFileSize(getFileSize(key)) }})</small>
                <small class="ml-2 text-muted">[{{ getFileExtension(getFileName(key)) }}]</small>
              </div>
            </div>
            <!--
            <b-button
              variant="outline-danger"
              size="sm"
              @click="removeFile(key)">
              <i class="feather icon-trash-2"></i>
            </b-button>
            -->
          </b-list-group-item>
        </b-list-group>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormFile,
  BFormInvalidFeedback,
  BAlert,
  BCard,
  BListGroup,
  BListGroupItem,
  BButton
} from 'bootstrap-vue'

export default {
  name: 'JuridicalDocumentationStep',
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormFile,
    BFormInvalidFeedback,
    BAlert,
    BCard,
    BListGroup,
    BListGroupItem,
    BButton
  },
  props: {
    formData: {
      type: Object,
      required: true
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    primaryVariant: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:form-data', 'upload-document', 'remove-file'],
  computed: {
    hasUploadedFiles() {
      return Object.values(this.formData).some(file => file !== null);
    },
    getFileName() {
      return (fieldName) => {
        const field = this.formData[fieldName];
        if (!field) return '';

        if (field.uploaded && field.metadata && field.metadata.original_name) {
          return field.metadata.original_name;
        }

        if (field.file_name) {
          return field.file_name;
        }

        return '<Nome do arquivo não encontrado>';
      };
    },
    isFileUploaded() {
      return (fieldName) => {
        const field = this.formData[fieldName];
        return field && field.uploaded && field.metadata && field.metadata.original_name;
      };
    },
    isFileUploading() {
      return (fieldName) => {
        const field = this.formData[fieldName];
        return field && field.file && field.file.name && !field.uploaded;
      };
    },
    getRadioGroupClass() {
      return !this.primaryVariant ? 'radio-group-dynamic-primary' : `radio-group-${this.primaryVariant}`;
    },
    uploadedFiles() {
      return Object.entries(this.formData)
        .filter(([_, file]) => file !== null)
        .reduce((acc, [key, file]) => {
          acc[key] = file;
          return acc;
        }, {});
    }
  },
  methods: {
    getFieldState(fieldName) {
      if (this.errors[fieldName]) {
        return false;
      }
      return null;
    },
    updateField(fieldName, file) {
      if (!file) return;

      // Emite o evento de upload para o componente pai
      this.$emit('upload-document', {
        fieldName,
        file,
        metadata: {
          original_name: file.name,
          size: file.size,
          type: file.type,
          last_modified: file.lastModified,
          document_type: fieldName
        }
      });

      // Atualiza o formData local com status de upload iniciado
      this.$emit('update:form-data', {
        [fieldName]: {
          file,
          uploaded: false,
          metadata: {
            original_name: file.name,
            size: file.size,
            type: file.type,
            last_modified: file.lastModified
          }
        }
      });
    },
    formatFileSize(size) {
      if (!size) return '';
      if (size < 1024) return size + ' B';
      if (size < 1024 * 1024) return (size / 1024).toFixed(1) + ' KB';
      return (size / (1024 * 1024)).toFixed(1) + ' MB';
    },
    removeFile(fieldName) {
      this.$emit('remove-file', fieldName);
    },
    getFileExtension(filename) {
      if (!filename) return '';
      const parts = filename.split('.');
      return parts.length > 1 ? parts.pop().toUpperCase() : '';
    },
    getDocumentTitle(key) {
      const titles = {
        statute: 'Estatuto / Ato Constitutivo',
        election_minutes: 'Ata de Eleição de Dirigentes',
        possession_minutes: 'Ata de Posse',
        alteration_minutes: 'Ata de Alteração',
        statute_alteration_minutes: 'Ata de Alteração de Estatuto',
        foundation_document: 'Documento de Fundação',
        cnpj_certificate: 'Certificado CNPJ',
        identification_members: 'Identificação dos integrantes do corpo dirigente de cada instituição de ensino'
      };
      return titles[key] || key;
    },
    getFileSize(key) {
      const file = this.formData[key];
      if (!file) return 0;

      // Se o arquivo já foi carregado com sucesso
      if (file.size) {
        return file.size;
      }

      // Se o arquivo está sendo carregado
      if (file.file) {
        return file.file.size;
      }

      return 0;
    },
    getFileName(key) {
      const file = this.formData[key];
      if (!file) return '';

      // Se o arquivo já foi carregado com sucesso
      if (file.file_name) {
        return file.file_name;
      }

      // Se o arquivo está sendo carregado
      if (file.file) {
        return file.file.name;
      }

      return '';
    },
    getJuridicalDocumentUrl(document) {
      if (!document) {
        return null;
      }

      // Tentar diferentes propriedades de URL - url_tmp primeiro
      if (document.url_tmp) {
        return document.url_tmp;
      }
      if (document.url_temp) {
        return document.url_temp;
      }
      if (document.url) {
        return document.url;
      }
      if (document.download_url) {
        return document.download_url;
      }

      // Verificar se há metadata com URLs
      if (document.metadata && document.metadata.url_tmp) {
        return document.metadata.url_tmp;
      }
      if (document.metadata && document.metadata.url_temp) {
        return document.metadata.url_temp;
      }
      if (document.metadata && document.metadata.url) {
        return document.metadata.url;
      }

      return null;
    }
  }
}
</script>

<style scoped>
.step-content {
  padding: 1rem 0;
}

/* Estilos para componentes de upload */
.form-file {
  margin-bottom: 0.5rem;
}

.form-text {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.form-text .feather {
  font-size: 0.8rem;
}

/* Estilo para lista de arquivos carregados */
.uploaded-files .card {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
}

.uploaded-files ul li {
  display: flex;
  align-items: center;
  padding: 0.25rem 0;
}

.uploaded-files .feather {
  flex-shrink: 0;
}

/* Estilos para drag and drop */
.custom-file-input:focus ~ .custom-file-label {
  border-color: var(--bs-primary, #007bff);
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Indicadores visuais para diferentes tipos de arquivo */
.text-success {
  color: #28a745 !important;
}
</style>
